import { getShowStudyPackViews } from "@helpers/exams";

/* ----------------- Helpers --------------- */
import { groupTagsByType, hasProductAccess } from "@helpers/content/tag";

/* ----------------- Types --------------- */
import type { MixedItemDist, MixedItemTag } from "@memorang/types";

/* ----------------- UI --------------- */
import { useDevice } from "@memorang/ui";

/* ----------------- Hooks --------------- */
import { useTagsStore } from "./useTagsStore";

import { useExamContext } from "@features/exam";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useAppTheme } from "@hooks/useAppTheme";
import { useProductMetadata } from "@hooks/useProduct";
/* ----------------- Router --------------- */
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";

const getMasteryKeyTopics = (
	mixedItemDist: MixedItemDist,
	hasAccess?: boolean,
	isMobile?: boolean,
	isDetailedList?: boolean,
) => {
	const getTagsToDisplay = (tags: MixedItemTag[]) => {
		if (isMobile && !isDetailedList) {
			const freeTags = tags.filter((tag) => !tag.locked);
			const premiumTags = tags.filter((tag) => tag.locked);
			return freeTags?.concat(premiumTags);
		}
		return tags.sort((a, b) => Number(a.locked) - Number(b.locked));
	};
	const { tags, freeTagIds } = mixedItemDist;
	const groupedTagsByType = groupTagsByType(
		tags,
		"Display",
		freeTagIds,
		hasAccess,
	);
	const allTags = Object.keys(groupedTagsByType).flatMap(
		(key) => groupedTagsByType[key],
	);
	const tagsToDisplay = getTagsToDisplay(allTags);
	return tagsToDisplay;
};
export const useMasteryKeyTopicsSectionData = (
	mixedItemDist?: MixedItemDist,
) => {
	const router = useRouter();

	const theme = useAppTheme();
	const { t } = useTranslation();
	const { data: productMetadata, isFetched: isProductMetadataFetched } =
		useProductMetadata();
	const { productUuid } = useAppConfigIds();

	const hasAccess = hasProductAccess(productMetadata, productUuid);
	const {
		currentExamContext: { studyPackProductId },
	} = useExamContext();

	const setCurrentSelectedMasteryItems = useTagsStore(
		(state) => state.setCurrentSelectedMasteryItems,
	);
	const { isMobile } = useDevice();
	const section = {
		data: [],
		type: "progress",
		onPress: undefined,
	};

	const isStudyPackEnabled = getShowStudyPackViews(studyPackProductId);

	if (!(mixedItemDist && isProductMetadataFetched)) {
		return {
			...section,
			sectionTitle: "Modules",
			loading: true,
			hasAccess,
		};
	}

	if (!isStudyPackEnabled) {
		return null;
	}

	const { tags } = mixedItemDist;

	const tagType = tags.find((tag) =>
		tag.displayType?.includes("Display"),
	)?.type;

	const masteryTopics = getMasteryKeyTopics(mixedItemDist, hasAccess, isMobile);

	const mappedMasteryTopics = masteryTopics.map((item) => {
		return {
			...item,
			locked: isStudyPackEnabled ? item.locked : false,
		};
	});

	return {
		...section,
		sectionTitle: "Modules",
		loading: false,
		hasAccess,
		data: mappedMasteryTopics,
		chipProps: hasAccess
			? undefined
			: {
					label: t("premium.chip"),
					isPremium: true,
					background: theme.palette.chips.premium,
				},
		onPress: () => {
			const items = getMasteryKeyTopics(
				mixedItemDist,
				hasAccess,
				isMobile,
				true,
			);
			setCurrentSelectedMasteryItems(items);
			router.push({
				pathname: "/progress/detailed-list/mastery",
				params: {
					type: tagType || "Topic",
				},
			});
		},
	};
};
